import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Users, Gift, Sparkles, Trophy } from "lucide-react";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [board, setBoard] = useState(null);

  const load = async () => {
    const { data } = await api.get("/referrals/me");
    setData(data);
  };
  useEffect(() => {
    load();
    api.get("/referrals/leaderboard").then(({ data }) => setBoard(data.leaderboard)).catch(() => setBoard([]));
  }, []);

  if (!data) return <div className="p-10 text-neutral-500">Loading…</div>;

  const link = `${window.location.origin}/register?ref=${data.code}`;
  const canRedeem = data.credit_usd >= data.redeem_threshold_usd;
  const remainingRefs = Math.max(0, Math.ceil((data.redeem_threshold_usd - data.credit_usd) / data.credit_per_referral_usd));

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };

  const redeem = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/referrals/redeem");
      toast.success(`Redeemed $${res.amount_redeemed_usd}. Access extended 30 days.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Grow the platform</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Refer &amp; earn</h1>
      <p className="text-neutral-400 mt-2 text-sm">
        Get <span className="text-white font-mono">${data.credit_per_referral_usd}</span> pending credit for every friend that signs up —
        it <span className="text-white">unlocks when they make their first payment</span>.
        Collect <span className="text-white font-mono">${data.redeem_threshold_usd}</span> of unlocked credit and redeem a free month of access.
      </p>
      <div className="mt-4 rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/[0.05] p-4 text-sm" data-testid="seller-referral-banner">
        <span className="text-[#FF3B30] font-bold">⚡ Sellers:</span>{" "}
        <span className="text-neutral-300">bring a seller who subscribes and get a <span className="text-white font-bold">FULL FREE MONTH</span> added to your access instantly — every single time.</span>
        {data.seller_free_months > 0 && (
          <span className="text-[#34C759] font-semibold"> You've earned {data.seller_free_months} free month{data.seller_free_months === 1 ? "" : "s"} so far.</span>
        )}
      </div>

      <div className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Your unique link</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="referral-link"
            readOnly
            value={link}
            className="bg-[#0A0A0A] border-[#27272A] font-mono text-sm"
            onFocus={(e) => e.target.select()}
          />
          <Button
            data-testid="referral-copy-btn"
            onClick={copy}
            className="bg-white text-black hover:bg-neutral-200 rounded-full h-10 px-4 whitespace-nowrap"
          >
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button
            data-testid="referral-whatsapp-btn"
            onClick={() => {
              const text = `⚡ Join me on VISMARKS — sell online with AI doing the work. Founding sellers (first 20) get their first month FREE with unlimited listings: ${link}`;
              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
            }}
            className="bg-[#25D366] hover:bg-[#1fb457] text-black rounded-full h-10 px-4 whitespace-nowrap font-semibold"
          >
            WhatsApp
          </Button>
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-neutral-500">
          Code: <span className="text-white font-mono tracking-wider">{data.code}</span>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="ambassador-pack">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Your ambassador pack</div>
        <p className="text-xs text-neutral-400 mb-4">Ready-made fliers + captions with <span className="text-white">your personal link</span> baked in — post them in your groups and earn on every signup.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { img: "/campaign/speed-flier.jpg", name: "0.2 Seconds", cap: `⚡ Your product can meet its buyer in 0.2 seconds on VISMARKS. I'm on it — join through my link: ${link}` },
            { img: "/campaign/about-square.jpg", name: "All About VISMARKS", cap: `Flat fees, verified sellers, AI that lists your products in 60 seconds — this is VISMARKS. Join me: ${link}` },
            { img: "/campaign/compare-protection.jpg", name: "Buyer Protection", cap: `🛡 On VISMARKS your money is protected until delivery. Shop and sell with confidence — sign up with my link: ${link}` },
          ].map((f) => (
            <div key={f.name} className="rounded-lg border border-[#27272A] overflow-hidden bg-black/30">
              <img src={f.img} alt={f.name} className="w-full h-28 object-cover" />
              <div className="p-2.5">
                <div className="text-[11px] font-bold text-white">{f.name}</div>
                <div className="mt-2 flex gap-1.5">
                  <button data-testid={`amb-copy-${f.name.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={async () => { await navigator.clipboard.writeText(f.cap); toast.success("Caption + your link copied"); }}
                    className="text-[10px] font-bold px-2.5 py-1 rounded-full border border-[#007AFF]/40 text-[#007AFF] hover:bg-[#007AFF]/10">
                    Copy caption
                  </button>
                  <a href={f.img} download className="text-[10px] font-bold px-2.5 py-1 rounded-full border border-neutral-700 text-neutral-300 hover:text-white">
                    Flier
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile icon={Users} label="Signups" value={data.referrals_count} />
        <Tile icon={Gift} label="Pending credit" value={`$${(data.pending_credit_usd || 0).toFixed(2)}`} accent="text-[#FF9F0A]" />
        <Tile icon={Gift} label="Unlocked credit" value={`$${data.credit_usd.toFixed(2)}`} accent="text-[#34C759]" />
        <Tile icon={Sparkles} label="To free month" value={canRedeem ? "READY" : `${remainingRefs} more`} accent={canRedeem ? "text-[#007AFF]" : ""} />
      </div>

      <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121212] p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div>
            <div className="font-bold">Redeem $100 credit for a free month</div>
            <div className="text-sm text-neutral-400 mt-1">Extends your access-fee window by 30 days — no card charged.</div>
          </div>
          <Button
            data-testid="referral-redeem-btn"
            disabled={!canRedeem || busy}
            onClick={redeem}
            className="bg-white text-black hover:bg-neutral-200 rounded-full h-11 px-5 disabled:opacity-40 disabled:hover:bg-white"
          >
            {busy ? "Redeeming…" : canRedeem ? "Redeem now" : "Not enough credit"}
          </Button>
        </div>
      </div>

      {data.history?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-black text-xl tracking-tight mb-3">Recent signups</h2>
          <div className="rounded-xl border border-[#27272A] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-xs uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Credit</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((h, i) => (
                  <tr key={i} className="border-t border-[#27272A]">
                    <td className="px-4 py-3 text-neutral-400">{new Date(h.at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${h.status === "earned" ? "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10" : "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10"}`}>
                        {h.status === "earned" ? "Unlocked" : "Pending first payment"}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${h.status === "earned" ? "text-[#34C759]" : "text-neutral-500"}`}>+${h.credit_awarded_usd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {board && board.length > 0 && (
        <div className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="referral-leaderboard">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[#FF9F0A]" />
            <h2 className="font-bold text-lg">Top referrers</h2>
          </div>
          <div className="mt-4 space-y-2">
            {board.map((r, i) => (
              <div key={i} data-testid={`leaderboard-row-${i}`} className="flex items-center gap-3 rounded-lg border border-[#27272A] bg-black/30 px-4 py-2.5">
                <span className={`font-mono font-bold w-6 ${i === 0 ? "text-[#FF9F0A]" : i < 3 ? "text-white" : "text-neutral-500"}`}>#{i + 1}</span>
                <span className="flex-1 text-sm font-semibold truncate">{r.name}</span>
                <span className="text-xs text-neutral-400">{r.confirmed_referrals} confirmed</span>
                {r.free_months_earned > 0 && (
                  <span className="text-xs text-[#34C759] font-semibold">{r.free_months_earned} free month{r.free_months_earned === 1 ? "" : "s"}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#121212] p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
        <Icon className="h-4 w-4 text-neutral-500" />
      </div>
      <div className={`mt-3 font-mono tabular-nums font-bold text-2xl ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}
